import type { ClientStatusApproval } from './types';
import {
  CLIENT_STATUS_DRY_RUN_LINE,
  clientStatusGroupLabel,
  clientStatusLine,
  clientStatusSubtitle,
  clientStatusTitle,
} from './clientStatusApprovalText';

interface ClientStatusApprovalCardProps {
  card: ClientStatusApproval;
  busy?: boolean;
  onApprove: () => void;
  onDecline: () => void;
}

/**
 * The card Emjae shows before it offboards or reactivates a client: which client, the status change,
 * and an Approve / Decline pair. Plain text only. A card that cannot be approved (unknown client, or
 * already in that state) has no Approve button at all and says why in the backend's own sentence.
 */
export function ClientStatusApprovalCard({ card, busy = false, onApprove, onDecline }: ClientStatusApprovalCardProps) {
  const subtitle = clientStatusSubtitle(card);
  return (
    <div
      role="group"
      aria-label={clientStatusGroupLabel(card)}
      className="mt-2 rounded-lg border border-warm-border bg-paper px-3 py-2.5"
    >
      {card.dryRun && (
        <p
          role="status"
          className="mb-2 rounded-md border border-warm-border bg-paper-2 px-2 py-1 font-mono text-[11px] text-ink-2"
        >
          {CLIENT_STATUS_DRY_RUN_LINE}
        </p>
      )}
      <p className="font-display text-[15px] leading-tight text-ink">{clientStatusTitle(card)}</p>
      {subtitle && <p className="mt-0.5 font-mono text-[11px] text-ink-2">{subtitle}</p>}
      <p className="mt-2 text-[12px] text-ink-2">{clientStatusLine(card)}</p>
      {card.notice && <p className="mt-2 text-[12px] text-ink">{card.notice}</p>}
      <div className="mt-3 flex gap-2">
        {!card.declineOnly && (
          <button
            type="button"
            disabled={busy}
            onClick={onApprove}
            className="rounded-md bg-coral px-2.5 py-1 text-[11px] font-medium text-white hover:bg-coral-hover disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            Approve
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={onDecline}
          className="rounded-md border border-warm-border bg-paper-2 px-2.5 py-1 text-[11px] font-medium text-ink-2 hover:border-warm-border-strong disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
        >
          Decline
        </button>
      </div>
    </div>
  );
}
